import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    if (!token) { setValidating(false); return; }
    fetch('https://anchorpals.com/api/auth/verify-reset-token?token=' + token)
      .then(r => r.json())
      .then(data => { setTokenValid(data.valid); setValidating(false); })
      .catch(() => { setTokenValid(false); setValidating(false); });
  }, [token]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('https://anchorpals.com/api/auth/reset-password', {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ token, password })
      });
      const data = await res.json();
      if (data.success) { setSuccess(true); setTimeout(() => navigate('/login'), 3000); }
      else setError(data.error?.message || 'Failed to reset password');
    } catch (err) { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  if (validating) return <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Validating...</div>;
  if (!token || !tokenValid) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f5f5f7',padding:'20px'}}>
      <div style={{background:'#fff',borderRadius:'20px',padding:'40px',maxWidth:'400px',width:'100%',textAlign:'center',boxShadow:'0 4px 20px rgba(0,0,0,0.08)'}}>
        <AlertCircle size={48} color="#ff3b30" style={{marginBottom:'16px'}} />
        <h2 style={{margin:'0 0 12px',color:'#1d1d1f'}}>Invalid or Expired Link</h2>
        <p style={{color:'#86868b',marginBottom:'24px'}}>This password reset link is invalid or has expired.</p>
        <Link to="/forgot-password" style={{color:'#0071e3',textDecoration:'none',fontWeight:'500'}}>Request a new link</Link>
      </div>
    </div>
  );

  if (success) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f5f5f7',padding:'20px'}}>
      <div style={{background:'#fff',borderRadius:'20px',padding:'40px',maxWidth:'400px',width:'100%',textAlign:'center',boxShadow:'0 4px 20px rgba(0,0,0,0.08)'}}>
        <CheckCircle size={48} color="#34c759" style={{marginBottom:'16px'}} />
        <h2 style={{margin:'0 0 12px',color:'#1d1d1f'}}>Password Reset!</h2>
        <p style={{color:'#86868b',marginBottom:'24px'}}>Your password has been reset. Redirecting to login...</p>
      </div>
    </div>
  );
  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f5f5f7',padding:'20px'}}>
      <div style={{background:'#fff',borderRadius:'20px',padding:'40px',maxWidth:'400px',width:'100%',boxShadow:'0 4px 20px rgba(0,0,0,0.08)'}}>
        <h1 style={{margin:'0 0 8px',fontSize:'28px',color:'#1d1d1f',textAlign:'center'}}>Create New Password</h1>
        <p style={{color:'#86868b',marginBottom:'24px',textAlign:'center'}}>Enter your new password below.</p>
        {error && <div style={{background:'#fff2f2',border:'1px solid #ffccc7',borderRadius:'12px',padding:'12px',marginBottom:'16px',display:'flex',alignItems:'center',gap:'8px'}}><AlertCircle size={18} color="#ff3b30"/><span style={{color:'#ff3b30',fontSize:'14px'}}>{error}</span></div>}
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom:'16px'}}>
            <label style={{display:'block',marginBottom:'8px',fontWeight:'500',color:'#1d1d1f'}}>New Password</label>
            <div style={{position:'relative'}}>
              <Lock size={18} style={{position:'absolute',left:'14px',top:'50%',transform:'translateY(-50%)',color:'#86868b'}} />
              <input type={showPassword?'text':'password'} value={password} onChange={(e)=>setPassword(e.target.value)} required placeholder="Min 6 characters" style={{width:'100%',padding:'14px 44px',border:'1px solid #d2d2d7',borderRadius:'12px',fontSize:'16px',outline:'none',boxSizing:'border-box'}} />
              <button type="button" onClick={()=>setShowPassword(!showPassword)} style={{position:'absolute',right:'14px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer'}}>{showPassword?<EyeOff size={18} color="#86868b"/>:<Eye size={18} color="#86868b"/>}</button>
            </div>
          </div>
          <div style={{marginBottom:'20px'}}>
            <label style={{display:'block',marginBottom:'8px',fontWeight:'500',color:'#1d1d1f'}}>Confirm Password</label>
            <div style={{position:'relative'}}>
              <Lock size={18} style={{position:'absolute',left:'14px',top:'50%',transform:'translateY(-50%)',color:'#86868b'}} />
              <input type={showPassword?'text':'password'} value={confirmPassword} onChange={(e)=>setConfirmPassword(e.target.value)} required placeholder="Confirm password" style={{width:'100%',padding:'14px 14px 14px 44px',border:'1px solid #d2d2d7',borderRadius:'12px',fontSize:'16px',outline:'none',boxSizing:'border-box'}} />
            </div>
          </div>
          <button type="submit" disabled={loading} style={{width:'100%',padding:'14px',background:loading?'#86868b':'#0071e3',color:'#fff',border:'none',borderRadius:'12px',fontSize:'16px',fontWeight:'600',cursor:loading?'not-allowed':'pointer'}}>{loading ? 'Resetting...' : 'Reset Password'}</button>
        </form>
      </div>
    </div>
  );
}
export default ResetPasswordPage;
